import { useEffect, useState } from "react";
import { motion, Reorder } from "framer-motion";
import { toast } from "sonner";
import { Plus, Trash2, GripVertical, Save, X } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { listItems, createItem, updateItem, deleteItem, reorderItems } from "@/lib/api";

const SCHEMAS = {
    publications: {
        title: "Publications",
        fields: [
            { key: "title", label: "Title", required: true },
            { key: "kind", label: "Kind (article | blog)", value: "article" },
            { key: "status", label: "Status (published | forthcoming | under_review)", value: "published" },
            { key: "venue", label: "Venue / Journal" },
            { key: "venue_detail", label: "Venue detail (vol, issue, indexing)" },
            { key: "year", label: "Year" },
            { key: "coauthors", label: "Co-authors" },
            { key: "url", label: "URL" },
            { key: "abstract", label: "Abstract", type: "textarea" },
        ],
        display: (p) => (
            <>
                <p className="font-display text-lg">{p.title}</p>
                <p className="font-mono text-[10px] uppercase tracking-widest text-paper/60 mt-1">{p.status || "published"} · {p.venue || "—"}{p.year ? ` · ${p.year}` : ""}</p>
            </>
        ),
    },
    projects: {
        title: "Projects",
        fields: [
            { key: "title", label: "Title", required: true },
            { key: "subtitle", label: "Subtitle" },
            { key: "description", label: "Description", type: "textarea" },
            { key: "tags", label: "Tags (comma-separated)", transform: (v) => (typeof v === "string" ? v.split(",").map((s) => s.trim()).filter(Boolean) : v), display: (v) => Array.isArray(v) ? v.join(", ") : (v || "") },
            { key: "image", label: "Image URL (or paste data URL)" },
        ],
        display: (p) => (
            <>
                <p className="font-display text-lg">{p.title}</p>
                <p className="font-mono text-[10px] uppercase tracking-widest text-paper/60 mt-1">{p.subtitle || ""}</p>
            </>
        ),
    },
    awards: {
        title: "Awards",
        fields: [
            { key: "title", label: "Title", required: true },
            { key: "issuer", label: "Issuer" },
            { key: "year", label: "Year" },
            { key: "description", label: "Description", type: "textarea" },
        ],
        display: (p) => (
            <>
                <p className="font-display text-lg">{p.title}</p>
                <p className="font-mono text-[10px] uppercase tracking-widest text-paper/60 mt-1">{p.issuer || "—"}{p.year ? ` · ${p.year}` : ""}</p>
            </>
        ),
    },
    courses: {
        title: "Courses",
        fields: [
            { key: "name", label: "Course name", required: true },
            { key: "institute", label: "Institute" },
            { key: "level", label: "Level" },
            { key: "description", label: "Description", type: "textarea" },
        ],
        display: (p) => (
            <>
                <p className="font-display text-lg">{p.name}</p>
                <p className="font-mono text-[10px] uppercase tracking-widest text-paper/60 mt-1">{p.institute || "—"}{p.level ? ` · ${p.level}` : ""}</p>
            </>
        ),
    },
    conferences: {
        title: "Conference Presentations",
        fields: [
            { key: "type", label: "Type (presented | attended)", value: "presented" },
            { key: "paper_title", label: "Paper title / Event title", required: true },
            { key: "conference_name", label: "Conference name" },
            { key: "location", label: "Location" },
            { key: "year", label: "Year" },
            { key: "url", label: "URL" },
        ],
        display: (p) => (
            <>
                <p className="font-display text-lg">{p.paper_title}</p>
                <p className="font-mono text-[10px] uppercase tracking-widest text-paper/60 mt-1">{p.type || "presented"} · {p.conference_name || "—"}{p.year ? ` · ${p.year}` : ""}</p>
            </>
        ),
    },
};

export default function AdminListManager({ kind }) {
    const schema = SCHEMAS[kind];
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // item currently being edited, or 'new'

    const refresh = async () => {
        setLoading(true);
        try {
            const list = await listItems(kind);
            setItems(list);
        } finally { setLoading(false); }
    };

    useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [kind]);

    const openNew = () => setEditing({ ...defaultFor(schema), __new: true });
    const openEdit = (item) => setEditing({ ...item });

    const onSave = async () => {
        try {
            const payload = normalize(editing, schema);
            if (editing.__new) {
                await createItem(kind, payload);
                toast.success("Created");
            } else {
                await updateItem(kind, editing.id, payload);
                toast.success("Updated");
            }
            setEditing(null);
            refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        }
    };

    const onDelete = async (item) => {
        if (!confirm(`Delete “${item.title || item.paper_title || item.name}”?`)) return;
        try {
            await deleteItem(kind, item.id);
            toast.success("Deleted");
            refresh();
        } catch { toast.error("Delete failed"); }
    };

    const onReorder = async (newList) => {
        setItems(newList);
        try { await reorderItems(kind, newList.map((i) => i.id)); }
        catch { toast.error("Reorder failed"); refresh(); }
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-paper/50">Manage</p>
                    <h1 className="mt-1 font-display text-3xl sm:text-4xl">{schema.title}</h1>
                </div>
                <Button onClick={openNew} className="bg-paper text-ink hover:bg-paper/90" data-testid={`admin-${kind}-create-button`}>
                    <Plus className="h-4 w-4 mr-2" /> New
                </Button>
            </div>

            {loading ? (
                <p className="text-paper/70">Loading…</p>
            ) : items.length === 0 ? (
                <Card className="p-8 text-center rounded-2xl bg-white/[0.03] border-white/10">
                    <p className="text-paper/70">No entries yet.</p>
                    <Button className="mt-4 bg-paper text-ink hover:bg-paper/90" onClick={openNew}>Add your first {kind.slice(0, -1)}</Button>
                </Card>
            ) : (
                <Reorder.Group axis="y" values={items} onReorder={onReorder} className="space-y-2">
                    {items.map((item) => (
                        <Reorder.Item key={item.id} value={item} whileDrag={{ scale: 1.01 }} className="list-none">
                            <Card className="p-4 rounded-xl bg-white/[0.04] border-white/10 flex items-center gap-3" data-testid={`admin-${kind}-item`}>
                                <div className="cursor-grab active:cursor-grabbing text-paper/40"><GripVertical className="h-4 w-4" /></div>
                                <div className="min-w-0 flex-1">{schema.display(item)}</div>
                                <div className="flex gap-2 shrink-0">
                                    <Button size="sm" variant="ghost" className="text-paper hover:bg-white/[0.06]" onClick={() => openEdit(item)} data-testid="admin-item-edit-button">Edit</Button>
                                    <Button size="sm" variant="ghost" className="text-paper/80 hover:text-red-300" onClick={() => onDelete(item)} data-testid="admin-item-delete-button"><Trash2 className="h-4 w-4" /></Button>
                                </div>
                            </Card>
                        </Reorder.Item>
                    ))}
                </Reorder.Group>
            )}

            <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
                <DialogContent className="bg-ink text-paper border-white/10 max-w-2xl">
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl">{editing?.__new ? `New ${kind.slice(0, -1)}` : `Edit ${kind.slice(0, -1)}`}</DialogTitle>
                    </DialogHeader>
                    {editing && (
                        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="grid gap-4 max-h-[65vh] overflow-y-auto pr-1">
                            {schema.fields.map((f) => (
                                <div key={f.key} className="space-y-1.5">
                                    <Label className="text-paper/80">{f.label}{f.required && <span className="text-red-300"> *</span>}</Label>
                                    {f.type === "textarea" ? (
                                        <Textarea rows={4} value={editing[f.key] ?? ""} onChange={(e) => setEditing((s) => ({ ...s, [f.key]: e.target.value }))} className="bg-white/[0.05] border-white/10 text-paper" />
                                    ) : (
                                        <Input value={f.display ? f.display(editing[f.key]) : (editing[f.key] ?? "")} onChange={(e) => setEditing((s) => ({ ...s, [f.key]: e.target.value }))} className="bg-white/[0.05] border-white/10 text-paper" />
                                    )}
                                </div>
                            ))}
                        </motion.div>
                    )}
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setEditing(null)} className="text-paper/80 hover:bg-white/[0.06]"><X className="h-4 w-4 mr-2" /> Cancel</Button>
                        <Button onClick={onSave} className="bg-paper text-ink hover:bg-paper/90" data-testid="admin-item-save-button"><Save className="h-4 w-4 mr-2" /> Save</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function defaultFor(schema) {
    const out = {};
    schema.fields.forEach((f) => { out[f.key] = f.value ?? ""; });
    return out;
}

function normalize(item, schema) {
    const copy = { ...item };
    delete copy.__new;
    schema.fields.forEach((f) => {
        if (f.transform) copy[f.key] = f.transform(copy[f.key]);
    });
    return copy;
}
